<template>
  <b-modal
    ref="modal"
    body-class="d-flex justify-content-center align-items-center flex-column"
    modal-class="lightbox"
    hide-footer
    @hidden="onModalHidden"
  >
    <template #modal-header>
      <div :class="headerClass">
        <button
          v-if="defaultCompare"
          class="btn btn-primary btn-sm compare"
          @click.stop="handleCompare"
        >
          <i class="bi bi-layout-split mr-1" /> {{ compareMode ? 'Preview' : 'Compare' }}
        </button>

        <div class="d-flex align-items-center title-buttons">
          <i
            v-if="!loading"
            class="bi bi-share mr-3"
            title="Copy share link"
            @click="handleShare"
          />
          <i
            v-if="!loading"
            class="bi bi-clipboard mr-3"
            title="Copy image (PNG/JPG)"
            @click="handleCopyToClipboard"
          />
          <i
            v-if="!loading"
            class="bi bi-download mr-3"
            :title="compareMode ? 'Download comparison' : 'Download'"
            @click="downloadHandler"
          />
          <i 
            class="bi bi-x" 
            style="font-size: 20px;" 
            @click="hideModal" 
          />
        </div>
      </div>
    </template>
    <b-spinner 
      v-show="loading" 
      label="Loading..." 
    />
    <div
      v-if="showLegibilitySingle"
      class="d-flex align-items-center justify-content-center mb-3"
    >
      <span 
        class="text-sm mr-2" 
        style="font-weight: 600;"
      >
        Mobile Legibility
      </span>
      <span 
        class="badge" 
        :class="getLegibilityBadgeClass(afterScore)"
      >
        {{ afterScore }}%
      </span>
    </div>
    <div 
      v-show="!loading && !compareMode" 
      class="image-wrapper"
    >
      <div class="image-with-prompt">
        <div class="image-container">
          <img
            :key="currentIndex"
            ref="previewImage"
            :src="currentImage.src"
            class="preview-img"
            @load="onLoad"
            @error="onMainImageError($event)"
          >

          <button 
            v-if="currentIndex > 0" 
            class="lightbox-nav left" 
            @click.stop="prev"
          >
            <i class="bi bi-chevron-left" />
          </button>

          <button
            v-if="currentIndex < images.length - 1"
            class="lightbox-nav right"
            @click.stop="next"
          >
            <i class="bi bi-chevron-right" />
          </button>
        </div>

        <div
          v-if="showThumbnails && images && images.length > 1"
          class="thumbnails-wrapper"
        >
          <div class="thumbnails-strip">
            <button
              v-for="(img, index) in images"
              :key="index"
              type="button"
              class="thumbnail-button"
              :class="{ active: index === currentIndex }"
              @click.stop="goTo(index)"
            >
              <img
                :src="img.src"
                alt=""
                class="thumbnail-image"
                @error="onThumbnailError(index, $event)"
              >
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="currentImage.prompt && !loading"
      class="mt-3 text-center"
    >
      <p
        ref="promptText"
        class="text-xs text-center prompt-text mb-1"
        :class="{ 'prompt-text--collapsed': !promptExpanded }"
      >
        <strong>Prompt:</strong> {{ currentImage.prompt }}
      </p>
      <button
        class="btn btn-link p-0 text-muted text-xs shadow-none"
        @click="promptExpanded = !promptExpanded"
      >
        {{ promptExpanded ? 'Show less' : 'Show more' }}
      </button>
    </div>
    <div 
      v-if="showLegibilityCompare" 
      class="w-100 mb-3"
    >
      <div class="text-center mb-2">
        <span style="font-weight: 600;">Mobile Legibility</span>
      </div>

      <div class="d-flex justify-content-around">
        <div 
          class="text-center" 
          style="flex: 1;"
        >
          <span class="text-sm mr-1"><strong>Before</strong></span>
          <span 
            class="badge" 
            :class="getLegibilityBadgeClass(beforeScore)"
          >
            {{ beforeScore != null ? `${beforeScore}%` : '--' }}
          </span>
        </div>

        <div 
          class="text-center" 
          style="flex: 1;"
        >
          <span class="text-sm mr-1"><strong>After</strong></span>
          <span 
            class="badge" 
            :class="getLegibilityBadgeClass(afterScore)"
          >
            {{ afterScore != null ? `${afterScore}%` : '--' }}
          </span>
        </div>
      </div>
    </div>
    <img
      v-if="compareMode && !loading && comparePreviewSrc"
      :src="comparePreviewSrc"
      class="comparison-preview"
    >
  </b-modal>
</template>
<script>
export default {
  name: 'LightboxModal',
  props: {
    previewSrc: {
      type: String,
      default: null
    },
    imagesFromParent: {
      type: Array,
      default: () => [],
    },
    startIndex: {
      type: Number,
      default: 0,
    },
    showThumbnails: {
      type: Boolean,
      default: false,
    },
    legibilityScore: {
      type: Number,
      default: null
    },
    originalLegibilityScore: {
      type: Number,
      default: null
    }
  },
  data: () => ({
    loading: false,
    secondImageSrc: null,
    compareMode: false,
    defaultCompare: false,
    images: [],
    currentIndex: 0,
    isSliderMode: false,
    imageFallback: '/img/icons/image-not-found.svg',
    promptExpanded: false,
  }),
  computed: {
    currentImage() {
      return this.images[this.currentIndex] || {};
    },
    headerClass() {
      return this.defaultCompare
        ? 'w-100 d-flex align-items-center justify-content-between'
        : 'w-100 d-flex align-items-center justify-content-end';
    },
    afterScore() {
      return this.isSliderMode ? this.currentImage.legibilityScore : this.legibilityScore;
    },
    beforeScore() {
      return this.isSliderMode ? this.currentImage.originalLegibilityScore : this.originalLegibilityScore;
    },

    showLegibilitySingle() {
      return !this.compareMode && !this.loading && this.afterScore != null;
    },

    showLegibilityCompare() {
      return this.compareMode && !this.loading && (this.beforeScore != null || this.afterScore != null);
    },

    comparePreviewSrc() {
      return this.isSliderMode ? this.currentImage.src : this.previewSrc;
    },

    downloadHandler() {
      return this.compareMode ? this.handleDownloadComparison : this.handleDownload;
    },
  },
  watch: {
    imagesFromParent: {
      immediate: true,
      handler(newImages) {
        if (Array.isArray(newImages) && newImages.length) {
          this.initializeFromParent(newImages, this.startIndex);
        }
      },
    },
    startIndex(newIndex) {
      if (Array.isArray(this.imagesFromParent) && this.imagesFromParent.length) {
        this.currentIndex = this.normalizeIndex(newIndex, this.imagesFromParent.length);
        this.loading = true;
      }
    },
  },
  methods: {
    getLegibilityBadgeClass(score) {
      if (score === null || score === undefined) return 'badge-secondary';
      if (score >= 70) return 'badge-success';
      if (score >= 50) return 'badge-warning';
      return 'badge-danger';
    },
    isSameUrl(a, b) {
      if (!a || !b) return a === b;
      try {
        return new URL(a, window.location.origin).href === new URL(b, window.location.origin).href;
      } catch (_) {
        return a === b;
      }
    },
    onMainImageError(event) {
      const el = event && event.target;
      if (!el) return;

      // Only apply fallback if the failed URL is the current slide's image (ignore stale/aborted loads)
      const currentSrc = this.images[this.currentIndex] && this.images[this.currentIndex].src;
      if (currentSrc && !this.isSameUrl(el.src, currentSrc)) {
        this.loading = false;
        return;
      }

      // Don't replace if we're already showing the fallback
      if (this.isSameUrl(el.src, this.imageFallback) || el.dataset.fallbackApplied === '1') {
        this.loading = false;
        return;
      }
      el.dataset.fallbackApplied = '1';

      el.src = this.imageFallback;

      if (this.images[this.currentIndex]) {
        this.$set(this.images, this.currentIndex, {
          ...this.images[this.currentIndex],
          src: this.imageFallback,
        });
      }
      this.loading = false;
    },
    onThumbnailError(index, event) {
      const el = event && event.target;
      if (!el) return;

      const thumbSrc = this.images[index] && this.images[index].src;
      if (thumbSrc && !this.isSameUrl(el.src, thumbSrc)) return;
      if (this.isSameUrl(el.src, this.imageFallback) || el.dataset.fallbackApplied === '1') return;

      el.dataset.fallbackApplied = '1';
      el.src = this.imageFallback;

      if (this.images[index]) {
        this.$set(this.images, index, {
          ...this.images[index],
          src: this.imageFallback,
        });
      }
    },
    normalizeIndex(index, length) {
      if (typeof index !== 'number' || Number.isNaN(index) || length <= 0) {
        return 0;
      }
      if (index < 0) return 0;
      if (index >= length) return length - 1;
      return index;
    },
    initializeFromParent(images, startIndex) {
      this.images = (images || []).map(img => (img && typeof img === 'object' ? { ...img } : { src: img }));
      this.currentIndex = this.normalizeIndex(startIndex, this.images.length);
      this.compareMode = false;
      this.defaultCompare = false;
      this.isSliderMode = true;
      this.loading = true;

      this.$nextTick(() => {
        if (this.$refs.modal) {
          this.$refs.modal.show();
        }
      });
    },
    goTo(index) {
      const normalized = this.normalizeIndex(index, this.images.length);
      if (normalized !== this.currentIndex) {
        this.currentIndex = normalized;
        this.loading = true;
      }
    },
    showMultiple({ images, currentIndex, mode = false }) {
      this.images = images;
      this.currentIndex = currentIndex;
      this.compareMode = mode;
      this.defaultCompare = mode;
      this.loading = true;
      this.isSliderMode = true;
      this.$refs.modal.show();
    },
    show(src, mode, prompt = null, isComparable = false) {
      this.images = [{
        src,
        prompt,
        legibilityScore: null,
        originalLegibilityScore: null
      }];
      this.currentIndex = 0;
      this.compareMode = mode;
      this.defaultCompare = isComparable;
      this.loading = true;
      this.isSliderMode = false;
      this.$refs.modal.show();
    },
    prev() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.loading = true;
        this.promptExpanded = false;
      }
    },

    next() {
      if (this.currentIndex < this.images.length - 1) {
        this.currentIndex++;
        this.loading = true;
        this.promptExpanded = false;
      }
    },
    onLoad () {
      this.loading = false
    },
    hideModal() {
      this.compareMode = false;
      this.$refs.modal.hide();
    },
    async handleDownload() {
      const downloadUrl = this.currentImage?.downloadUrl || this.currentImage?.src;
      if (!downloadUrl) return;

      try {
        const response = await fetch(downloadUrl);
        if (!response.ok) throw new Error(response.statusText);
        const blob = await response.blob();

        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = blobUrl;
        link.download = this.getFilename(downloadUrl);
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (err) {
        console.warn('[Lightbox] Download failed, opening in new tab:', err);
        window.open(downloadUrl, '_blank');
      }
    },
    getFilename(url) {
      if (!url) return 'download';
      try {
        const u = new URL(url, window.location.origin);
        const fileParam = u.searchParams.get('file');
        if (fileParam) {
          const name = fileParam.split('/').pop();
          if (name) return name;
        }
      } catch (_) {
        // Ignore URL parsing errors
      }
      const fromPath = url.split('/').pop() || '';
      const base = (fromPath.split('?')[0] || '').trim();
      return base || 'download';
    },
    async buildComparison() {
      this.$emit('buildComparison');
    },
    handleDownloadComparison() {
      this.$emit('handleDownloadComparison');
    },
    async handleCompare() {
      this.loading = true;
      this.compareMode = !this.compareMode;

      await this.$nextTick();
      await new Promise(requestAnimationFrame);

      this.$emit('buildComparison', () => {
        this.loading = false;
      });
    },
    onModalHidden() {
      this.$emit('hidden');
    },
    // Clipboard / share helpers
    async handleCopyToClipboard() {
      const src = this.currentImage?.src;
      if (!src) return;

      const copyableFormats = ['png', 'jpg', 'jpeg', 'gif'];
      const extension = src.split('.').pop()?.toLowerCase();

      // Check if image type is copyable
      const isRasterImage = copyableFormats.includes(extension);

      try {
        if (!isRasterImage) {
          // Unsupported formats (PSD, SVG, WEBP fallback)
          await this.copyLinkToClipboard(src, `Copied link to image to the clipboard.`);
          return;
        }

        // Clipboard API support check
        if (window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
          await this.copyImageToClipboard(src);
          return;
        }

        // Fallback: copy URL
        await this.copyLinkToClipboard(src, 'Your browser doesn’t support copying images. Link copied instead.');
      } catch (err) {
        console.error('[Clipboard] Unexpected error', err);
        await this.copyLinkToClipboard(src, 'Copied link to image to the clipboard.');
      }
    },
    async copyImageToClipboard(src) {
      try {
        const res = await fetch(src, { mode: 'cors' });
        if (!res.ok) throw new Error(`Fetch failed with status ${res.status}`);

        const blob = await res.blob();

        // Special warning for WEBP
        if (blob.type === 'image/webp') {
          console.warn('[Clipboard] WEBP cannot be copied directly. Fallback to link.');
          await this.copyLinkToClipboard(src, 'WEBP image cannot be copied. Link copied instead.');
          return;
        }

        await navigator.clipboard.write([
          new ClipboardItem({ [blob.type || 'image/png']: blob })
        ]);

        this.notifyCopied('Image copied to clipboard', 'success');
      } catch (err) {
        console.warn('[Clipboard] Image copy failed (browser or CORS)', err);
        await this.copyLinkToClipboard(src, 'Copied link to image to the clipboard.');
      }
    },
    async copyLinkToClipboard(src, message) {
      try {
        // Validate URL
        const url = new URL(src, window.location.origin).href;
        await navigator.clipboard.writeText(url);
        this.notifyCopied(message, 'warning');
      } catch (err) {
        console.error('[Clipboard] Failed to copy link', err);
        this.notifyCopied('Clipboard blocked. Cannot copy.', 'error');
      }
    },
    async handleShare() {
      if (!this.currentImage.src) return;
      try {
        const url = new URL(this.currentImage.src, window.location.origin).href;
        await navigator.clipboard.writeText(url);
        this.notifyCopied('Share link copied', 'success');
      } catch (err) {
        console.error('[Share] Failed to copy link', err);
        this.notifyCopied('Failed to copy share link', 'error');
      }
    },

    notifyCopied(message, variant) {
      const VARIANT_TITLES = { success: 'Success', warning: 'Warning', error: 'Error' };
      const title = VARIANT_TITLES[variant] || 'Info';

      this.$bvToast?.toast(message, {
        title,
        variant,
        solid: true
      });
    }
  },
}
</script>

<style scoped lang="scss">
::v-deep .modal-dialog {
  min-width: 500px;
  max-width: calc(100vw - 40px);
  max-height: calc(100vh - 40px);
  width: fit-content;
}
.preview-img {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain; 
}
::v-deep .lightbox .modal-content {
  border-radius: 8px !important;
}
::v-deep .lightbox .modal-header {
  border-bottom: 0 !important;
  padding-bottom: 0 Im !important;
}
::v-deep .title-buttons i {
    opacity: 0.7;
    cursor: pointer;
    transition: opacity 0.15s ease;
}
::v-deep .title-buttons i:hover {
    opacity: 1;
}
::v-deep .lightbox {
    background-color: rgba(0 0 0 / 0.8);
}
.comparison-canvas {
  max-width: 100%;
  max-height: 80vh;
}

.prompt-text {
  word-break: break-word;
}

.prompt-text--collapsed {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.comparison-preview {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
}
::v-deep .b-spinner {
  z-index: 10;
}

.badge-success {
  background-color: #28a745;
  color: white;
}
.badge-warning {
  background-color: #ffc107;
  color: #212529;
}
.badge-danger {
  background-color: #dc3545;
  color: white;
}
.badge-secondary {
  background-color: #6c757d;
  color: white;
}

.image-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
}

.image-with-prompt {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.image-container {
  position: relative;
  width: min(900px, 80vw);
  height: 60vh;          
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-img,
.comparison-preview {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  min-height: 0;        
  object-fit: contain;
}


.lightbox-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  backdrop-filter: blur(4px);
  background: rgba(0, 0, 0, 0.45);
  color: white;
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 20;
  opacity: 0;
}

.image-container:hover .lightbox-nav {
  opacity: 1;
}

.lightbox-nav.left { left: 12px; }
.lightbox-nav.right { right: 12px; }


.thumbnails-wrapper {
  width: 100%;
  margin-top: 12px;
  display: flex;
  justify-content: center;
}

.thumbnails-strip {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: min(900px, 80vw);
  padding: 4px 2px;
  overflow-x: auto;
}

.thumbnail-button {
  border: none;
  padding: 0;
  background: transparent;
  border-radius: 4px;
  overflow: hidden;
  width: 64px;
  height: 48px;
  flex: 0 0 auto;
  opacity: 0.6;
  cursor: pointer;
  transition: opacity 0.15s ease-in-out, transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out, border-color 0.15s ease-in-out;
  border: 2px solid transparent;
}

.thumbnail-button:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.thumbnail-button.active {
  opacity: 1;
  border-color: #10b981;
  box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.4);
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

::v-deep .lightbox {
  display: flex !important;
  align-items: center !important; 
  justify-content: center !important; 
  padding: 0 !important; 
}

::v-deep .modal-dialog {
  width: 80vw;
  max-width: 900px;
  min-width: 500px;
  height: auto;        
  max-height: 80vh;
  margin: 0;          
}
</style>
